import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPasswordPolicy,
  updatePasswordPolicy,
} from '../features/iso27001/security/securityApi';
import type { PasswordPolicy } from '../features/iso27001/security/types';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { showToast } from '../components/ui/toast.utils';
import { AppDispatch, RootState } from '../store/store';
import { loadRoles } from '../features/roles/rolesSlice';

const BASE_MFA_ROLES = ['superadmin', 'admin'];

export function PasswordPolicyPage() {
  const { canEdit } = usePlatformAccessLevel('security.password_policy.access');
  const dispatch = useDispatch<AppDispatch>();
  const roles = useSelector((state: RootState) => state.roles.items ?? []);
  const [policy, setPolicy] = useState<PasswordPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mfaRoleOptions = useMemo(() => {
    const custom = roles.map((role) => role.name).filter(Boolean);
    return Array.from(new Set([...BASE_MFA_ROLES, ...custom]));
  }, [roles]);

  const toggleMfaRole = (role: string) => {
    setPolicy((prev) => {
      if (!prev) return prev;
      const current = prev.mfaRequiredForRoles ?? [];
      const next = current.includes(role)
        ? current.filter((item) => item !== role)
        : [...current, role];
      return { ...prev, mfaRequiredForRoles: next };
    });
  };

  useEffect(() => {
    let cancelled = false;
    void dispatch(loadRoles({ page: 1, pageSize: 200, sortBy: 'name', sortDir: 'asc' }));
    const load = async () => {
      try {
        const data = await fetchPasswordPolicy();
        if (!cancelled) {
          setPolicy(data);
        }
      } catch {
        if (!cancelled) {
          showToast('No se pudo cargar la política de contraseñas', 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const updateField = <K extends keyof PasswordPolicy>(key: K, value: PasswordPolicy[K]) => {
    setPolicy((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!policy || !canEdit) return;
    setSaving(true);
    try {
      const updated = await updatePasswordPolicy(policy);
      setPolicy(updated);
      showToast('Política de contraseñas actualizada', 'success');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      showToast(axiosErr.response?.data?.error || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !policy) {
    return (
      <div className='p-6 text-muted-foreground'>Cargando política de contraseñas...</div>
    );
  }

  return (
    <div className='mx-auto max-w-2xl space-y-6 p-6'>
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>Política de contraseñas</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Requisitos de complejidad, expiración, bloqueo por intentos fallidos y MFA obligatorio por rol (ISO 27001 A.5.17).
        </p>
      </div>

      <div className='space-y-4 border border-border rounded-lg p-4'>
        <div>
          <label htmlFor='minLength' className='block text-sm font-medium mb-1'>
            Longitud mínima
          </label>
          <input
            id='minLength'
            type='number'
            min={8}
            max={64}
            disabled={!canEdit}
            value={policy.minLength}
            onChange={(e) => updateField('minLength', Number(e.target.value))}
            className='w-full border border-border rounded-md px-3 py-2 bg-background'
          />
        </div>

        {(
          [
            ['requireUppercase', 'Requiere mayúscula'],
            ['requireLowercase', 'Requiere minúscula'],
            ['requireDigit', 'Requiere dígito'],
            ['requireSpecialChar', 'Requiere carácter especial'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className='flex items-center gap-2 text-sm'>
            <input
              type='checkbox'
              disabled={!canEdit}
              checked={policy[key]}
              onChange={(e) => updateField(key, e.target.checked)}
            />
            {label}
          </label>
        ))}

        <div>
          <label htmlFor='expirationDays' className='block text-sm font-medium mb-1'>
            Expiración (días, vacío = sin expiración)
          </label>
          <input
            id='expirationDays'
            type='number'
            min={1}
            max={365}
            disabled={!canEdit}
            value={policy.expirationDays ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              updateField('expirationDays', raw === '' ? null : Number(raw));
            }}
            className='w-full border border-border rounded-md px-3 py-2 bg-background'
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label htmlFor='maxFailedAttempts' className='block text-sm font-medium mb-1'>
              Intentos fallidos antes de bloqueo
            </label>
            <input
              id='maxFailedAttempts'
              type='number'
              min={3}
              max={20}
              disabled={!canEdit}
              value={policy.maxFailedAttempts}
              onChange={(e) => updateField('maxFailedAttempts', Number(e.target.value))}
              className='w-full border border-border rounded-md px-3 py-2 bg-background'
            />
          </div>
          <div>
            <label htmlFor='lockoutMinutes' className='block text-sm font-medium mb-1'>
              Minutos de bloqueo
            </label>
            <input
              id='lockoutMinutes'
              type='number'
              min={5}
              max={1440}
              disabled={!canEdit}
              value={policy.lockoutMinutes}
              onChange={(e) => updateField('lockoutMinutes', Number(e.target.value))}
              className='w-full border border-border rounded-md px-3 py-2 bg-background'
            />
          </div>
        </div>

        <div>
          <p className='block text-sm font-medium mb-2'>MFA TOTP obligatorio para roles</p>
          <p className='text-xs text-muted-foreground mb-2'>
            Los usuarios con estos roles deben tener MFA activo antes de ingresar.
          </p>
          <div className='space-y-2'>
            {mfaRoleOptions.map((role) => (
              <label key={role} className='flex items-center gap-2 text-sm'>
                <input
                  type='checkbox'
                  disabled={!canEdit}
                  checked={(policy.mfaRequiredForRoles ?? []).includes(role)}
                  onChange={() => toggleMfaRole(role)}
                />
                {role}
              </label>
            ))}
          </div>
        </div>
      </div>

      {canEdit && (
        <button
          type='button'
          onClick={() => void handleSave()}
          disabled={saving}
          className='px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50'
        >
          {saving ? 'Guardando...' : 'Guardar política'}
        </button>
      )}
    </div>
  );
}
